<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /**
     * The heading element to use (e.g. `h1`, `h2`, etc.).
     */
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p'

    /**
     * The size of the heading.
     */
    size?:
      | 'xs'
      | 'sm'
      | 'md'
      | 'lg'
      | 'xl'
      | '2xl'
      | '3xl'
      | '4xl'
      | '5xl'
      | '6xl'
      | '7xl'
      | '8xl'
      | '9xl'

    /**
     * The weight of the heading.
     */
    weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold'

    /**
     * The spacing below the heading.
     */
    lead?: 'none' | 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose'
  }>(),
  {
    as: 'p',
    size: 'xl',
    weight: 'semibold',
    lead: 'normal',
    color: undefined,
  },
)

// Tailwind class mappings
const sizeClasses = {
  'xs': 'text-xs',
  'sm': 'text-sm',
  'md': 'text-base',
  'lg': 'text-lg',
  'xl': 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
  '6xl': 'text-6xl',
  '7xl': 'text-7xl',
  '8xl': 'text-8xl',
  '9xl': 'text-9xl',
}

const weightClasses = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
}

const leadClasses = {
  none: 'leading-none',
  tight: 'leading-tight',
  snug: 'leading-snug',
  // Override the normal line height to match design (1.2)
  normal: 'leading-[1.2]',
  relaxed: 'leading-relaxed',
  loose: 'leading-loose',
}

const colorClasses = {
  theme: 'text-theme-content dark:text-theme-content-inverted',
  white: 'text-white',
}

const classes = computed(() => [
  // Base classes
  // 'font-heading',
  // Theme colors with dark mode support
  // 'text-theme-content dark:text-theme-content-inverted',
  // Size classes
  sizeClasses[props.size as keyof typeof sizeClasses],
  // Weight classes
  weightClasses[props.weight as keyof typeof weightClasses],
  // Leading classes
  leadClasses[props.lead as keyof typeof leadClasses],
  // Color classes
  // colorClasses[props.color as keyof typeof colorClasses],
])
</script>

<template>
  <component
    :is="props.as"
    :class="classes"
    class="font-heading"
  >
    <slot />
  </component>
</template>
